import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  useSidebar,
} from "@/components/ui/sidebar";
import DetailHeaderBar from "components/DetailHeaderBar";
import { SidebarMobileTriggerBar } from "components/sidebar/SidebarMobileTrigger";
import SidebarToggleButton from "components/sidebar/SidebarToggleButton";
import {
  SIDEBAR_CONTENT_CLASSES,
  SIDEBAR_GROUP_LABEL_CLASSES,
  SIDEBAR_MENU_BUTTON_CLASSES,
  SIDEBAR_STYLE,
} from "components/sidebar/sidebarShell";
import { useAppTabs } from "hooks/useAppTabs";

// The section list is a separate component so it can read the sidebar context
// that SidebarDetailLayout creates and close the mobile sheet after a section is
// selected; otherwise the selected section renders behind the sheet.
const SectionMenu = ({ groups, activeKey, onSelect }) => {
  const { isMobile, setOpenMobile } = useSidebar();

  const selectSection = key => {
    onSelect(key);
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <>
      {groups.map((group, index) => (
        <SidebarGroup key={group.label ?? `group-${index}`}>
          {group.label && (
            <SidebarGroupLabel className={SIDEBAR_GROUP_LABEL_CLASSES}>
              {group.label}
            </SidebarGroupLabel>
          )}
          {group.action && (
            <div className="tw:absolute tw:top-2 tw:right-2 tw:z-10 tw:group-data-[collapsible=icon]:hidden">
              {group.action}
            </div>
          )}
          <SidebarMenu>
            {group.items.map(({ key, label, icon: Icon, badge }) => (
              <SidebarMenuItem key={key}>
                <SidebarMenuButton
                  isActive={key === activeKey}
                  onClick={() => selectSection(key)}
                  className={SIDEBAR_MENU_BUTTON_CLASSES}
                  tooltip={label}
                  size="sm"
                >
                  {Icon && <Icon />}
                  <span>{label}</span>
                </SidebarMenuButton>
                {badge && <SidebarMenuBadge>{badge}</SidebarMenuBadge>}
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
          {group.note && (
            <SidebarGroupContent className="tw:px-2 tw:pt-1 tw:group-data-[collapsible=icon]:hidden">
              {group.note}
            </SidebarGroupContent>
          )}
        </SidebarGroup>
      ))}
    </>
  );
};

// Generic record-detail shell: a left navigation rail plus a content area that
// shows the selected section. It fills its parent without imposing a height or
// an extra scroll container, so the section body (e.g. a table) owns its own
// scrolling. Sections are URL-driven — the active one is the `:tab` route param,
// and selecting a section navigates to `${basePath}/${key}` so back/forward and
// deep links work.
//
// Section shape: { key, label, icon, badge?, content, hidden?, hideFromSidebar?,
// group?, groupAction?, groupNote? }. `badge` renders in the item's badge slot.
// Hidden sections (ACL / feature-flag gating) are filtered out before rendering.
// A `hideFromSidebar` section still resolves and renders on its
// `${basePath}/${key}` route, but is left out of the rail — used for a section
// reached only by a deep link or a header action. Consecutive sections sharing a
// `group` render under one labelled sidebar group; the group's `action` (rendered
// beside the title) and `note` (a muted sub-line under the menu) are taken from
// the section that opens the group, so callers set them on every section of the
// group.
//
// The collapse trigger is built into the rail header, so every consumer gets a
// consistent collapse control without wiring one up. `header` is an optional rail
// slot rendered on the left of that row with the trigger pushed to the right; a
// string renders as the standard rail title, a node (e.g. an account switcher)
// renders as is. The row stacks in icon-collapse mode. On mobile the rail opens
// from a bar labelled `mobileMenuLabel`, "<header> Navigation" for a string
// header. `breadcrumb`
// and `title` drive an optional content-header band above the section body; the
// band renders only when at least one is passed. `titleActions` is an optional
// node rendered on the right of that band, opposite the title.
//
// `info` is an optional read-only panel rendered in the rail below the section
// menu under an `infoLabel` heading; it is hidden in icon-collapse mode.
//
// `collapsible` defaults to the primitive's icon collapse, so the rail shrinks to
// icons via the built-in trigger (or Cmd/Ctrl+B); pass "none" for a static rail.
// The collapsed preference is one shared cookie across detail pages. Under the
// global rail the primitive binds the shortcut once per provider, so Cmd/Ctrl+B
// toggles both rails.
export default function SidebarDetailLayout({
  sections,
  basePath,
  defaultSection,
  header = null,
  mobileMenuLabel,
  breadcrumb = null,
  title = null,
  titleActions = null,
  info = null,
  infoLabel = "Details",
  collapsible = "icon",
}) {
  const visibleSections = sections.filter(section => !section.hidden);
  const fallback = defaultSection ?? visibleSections[0]?.key ?? "";
  const { activeTab, handleTabChange } = useAppTabs({
    basePath,
    defaultTab: fallback,
  });

  if (visibleSections.length === 0) {
    return null;
  }

  const activeSection =
    visibleSections.find(section => section.key === activeTab) ??
    visibleSections[0];

  const navigationLabel =
    mobileMenuLabel ??
    (typeof header === "string" ? `${header} Navigation` : undefined);

  const menuSections = visibleSections.filter(
    section => !section.hideFromSidebar
  );

  const groups = menuSections.reduce((acc, section) => {
    const label = section.group ?? null;
    const last = acc[acc.length - 1];
    if (last && last.label === label) {
      last.items.push(section);
    } else {
      acc.push({
        label,
        items: [section],
        action: section.groupAction ?? null,
        note: section.groupNote ?? null,
      });
    }
    return acc;
  }, []);

  return (
    <SidebarProvider
      className="tw:h-full tw:min-h-0 tw:min-w-0 tw:flex-1 tw:overflow-hidden tw:[transform:translateZ(0)]"
      style={SIDEBAR_STYLE}
    >
      <Sidebar
        collapsible={collapsible}
        className="tw:shrink-0"
        role="navigation"
        aria-label={navigationLabel ?? "Section navigation"}
      >
        <SidebarHeader className="tw:flex-row tw:items-center tw:justify-between tw:gap-2 tw:group-data-[collapsible=icon]:flex-col tw:group-data-[collapsible=icon]:items-center">
          {typeof header === "string" ? (
            <span className="tw:min-w-0 tw:truncate tw:font-heading tw:text-sm tw:font-semibold tw:text-sidebar-foreground tw:group-data-[collapsible=icon]:hidden">
              {header}
            </span>
          ) : (
            header
          )}
          <SidebarToggleButton />
        </SidebarHeader>
        <SidebarContent className={SIDEBAR_CONTENT_CLASSES}>
          <SectionMenu
            groups={groups}
            activeKey={activeSection.key}
            onSelect={key => handleTabChange(null, { name: key })}
          />
          {info && (
            <SidebarGroup className="tw:group-data-[collapsible=icon]:hidden">
              <SidebarGroupLabel className={SIDEBAR_GROUP_LABEL_CLASSES}>
                {infoLabel}
              </SidebarGroupLabel>
              <SidebarGroupContent className="tw:flex tw:flex-col tw:gap-4 tw:px-2 tw:pt-1">
                {info}
              </SidebarGroupContent>
            </SidebarGroup>
          )}
        </SidebarContent>
      </Sidebar>
      <div className="tw:flex tw:min-h-0 tw:min-w-0 tw:flex-1 tw:flex-col">
        <SidebarMobileTriggerBar label={navigationLabel} />
        {(breadcrumb || title || titleActions) && (
          <DetailHeaderBar
            breadcrumb={breadcrumb}
            title={title}
            actions={titleActions}
          />
        )}
        <div
          data-slot="detail-pane"
          className="tw:relative tw:flex tw:min-h-0 tw:min-w-0 tw:flex-1 tw:flex-col"
        >
          {/* The gutter stays reserved so content whose height changes, such
              as a filtered Activity Log, does not shift sideways as the
              scrollbar comes and goes. */}
          <div className="tw:flex tw:min-h-0 tw:min-w-0 tw:flex-1 tw:flex-col tw:overflow-y-auto tw:p-4 tw:scrollbar-gutter-stable">
            {activeSection.content}
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
}
